import { type MouseEvent } from 'react';
import { type FooterNavLink } from '~/vendor/cx-web/domain-types';
import { trackEvent } from '~/vendor/cx-web/feature-tracking/dataLayer';
import { normalizeSchemeHref } from '~/vendor/cx-web/utils/formatLinkProperties';
import classnames from 'classnames';

import { type CmpProps } from '../../types';

import styles from './FooterGeneralLinks.module.scss';

const GeneralLink = ({ link, name }: Pick<FooterNavLink, 'link' | 'name'>) => (
  <a
    className={styles.generalLinksNavItem}
    data-testid="footer-general-link"
    href={normalizeSchemeHref(link)}
    onClick={() => trackEvent('footer_level_three_nav', { selection: name })}
  >
    {name}
  </a>
);

const GeneralLinkWithClickEvent = ({
  name,
  onClick,
}: Partial<FooterNavLink> & { onClick: (e: MouseEvent) => void }) => (
  <button
    aria-label={`Open ${name}`}
    className={styles.generalLinksNavItem}
    data-testid="footer-general-link"
    // Track before delegating — the Admiral handoff can throw. See FooterGeneralLinks.test.tsx.
    onClick={e => {
      trackEvent('footer_level_three_nav', { selection: name });
      onClick(e);
    }}
    type="button"
  >
    {name}
  </button>
);

const shouldNotShowLinks = (links: FooterNavLink[]) => {
  return !links?.length;
};

type FooterGeneralLinksProps = {
  cmpProps?: CmpProps;
  isAdmiralCookieBannerEnabled?: boolean;
  isUkFooterStyle?: boolean;
  levelThree: FooterNavLink[];
};

export const FooterGeneralLinks = ({
  cmpProps,
  isAdmiralCookieBannerEnabled,
  isUkFooterStyle,
  levelThree,
}: FooterGeneralLinksProps) => {
  const isCmpEnabled = isAdmiralCookieBannerEnabled;
  const generalLinks = levelThree || [];

  if (shouldNotShowLinks(generalLinks) && !isCmpEnabled) return null;

  const linksNav = (
    <nav
      aria-label="Footer General Links Navigation"
      className={classnames(styles.generalLinksNavContainer, isUkFooterStyle && styles.generalLinksNavContainerUk)}
    >
      {generalLinks?.map(({ link, name }) => (
        <GeneralLink key={name} link={link} name={name} />
      ))}
      {isCmpEnabled && cmpProps && (
        <GeneralLinkWithClickEvent key={cmpProps.key} name={cmpProps.name} onClick={cmpProps.onClick} />
      )}
    </nav>
  );

  return (
    <section
      aria-label="Footer General Links Section"
      className={classnames(styles.generalLinksWrapper, isUkFooterStyle && styles.generalLinksWrapperUk)}
      data-testid="footer-general-links-container"
    >
      {isUkFooterStyle ? (
        <div className={styles.generalLinksRowUk}>
          {linksNav}
          <div className={styles.generalLinksCopyrightUk} data-testid="footer-copyright">
            © Copyright {new Date().getUTCFullYear()}
          </div>
        </div>
      ) : (
        linksNav
      )}
    </section>
  );
};
